// One match card in normal page flow (Swiss rounds, group matchdays). Same look, status,
// scores and drag-to-swap as the elimination tree cards (EliminationView).
import React from 'react';
import { useTranslation } from 'react-i18next';
import TeamLogo from '../teams/TeamLogo';
import { cardStatus, statusLabel, scoresOf } from './bracketText';

/** dnd = { dragSlot, setDragSlot, dropKey, setDropKey, onSwap, canMove(m, slot) } (admin only) */
export default function MatchCard({ m, teams, readOnly, selected, onSelect, linkPrefix = '/match', dnd, hoverTeam, setHoverTeam }) {
  const { t } = useTranslation();
  const st = cardStatus(m);
  const sc = scoresOf(m);
  const clickable = !readOnly || !!m.match_id;

  const row = (slot) => {
    const id = m[`team${slot}_id`];
    const tm = id ? teams[id] : null;
    const won = m.status === 'completed' && m.winner_id && m.winner_id === id;
    const lost = m.status === 'completed' && m.winner_id && id && m.winner_id !== id;
    const can = !!(dnd && dnd.canMove(m, slot));
    const key = `${m.id}:${slot}`;
    const isSrc = dnd && dnd.dragSlot && dnd.dragSlot.match_id === m.id && dnd.dragSlot.slot === slot;
    return (
      <div className={`br-t${won ? ' win' : ''}${lost ? ' lose' : ''}${hoverTeam && id === hoverTeam ? ' hl' : ''}${can ? ' movable' : ''}${dnd && dnd.dropKey === key ? ' drop' : ''}${isSrc ? ' src' : ''}`}
        onMouseEnter={() => id && setHoverTeam && setHoverTeam(id)} onMouseLeave={() => setHoverTeam && setHoverTeam(null)}
        draggable={can}
        title={can ? t('bracket.dragHint', 'Drag onto another team to swap them') : undefined}
        onDragStart={can ? (e) => { e.stopPropagation(); e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', key); dnd.setDragSlot({ match_id: m.id, slot }); } : undefined}
        onDragEnd={dnd ? () => { dnd.setDragSlot(null); dnd.setDropKey(null); } : undefined}
        onDragOver={can && dnd.dragSlot && !isSrc ? (e) => { e.preventDefault(); if (dnd.dropKey !== key) dnd.setDropKey(key); } : undefined}
        onDragLeave={dnd ? () => dnd.dropKey === key && dnd.setDropKey(null) : undefined}
        onDrop={can && dnd.dragSlot && !isSrc ? (e) => { e.preventDefault(); e.stopPropagation(); const src = dnd.dragSlot; dnd.setDragSlot(null); dnd.setDropKey(null); dnd.onSwap(src, { match_id: m.id, slot }); } : undefined}>
        <span className="seed">{tm && tm.seed ? tm.seed : ''}</span>
        {tm ? <TeamLogo team={tm} size={18} radius={4} /> : null}
        {tm ? <span className="nm" title={tm.name}>{tm.name}</span> : <span className="tbd">{st === 'bye' || st === 'walkover' ? t('bracket.bye', 'BYE') : t('bracket.tbd', 'TBD')}</span>}
        {sc ? <span className="sc">{sc[slot - 1]}</span> : null}
      </div>
    );
  };

  return (
    <div className={`br-m flow${readOnly ? ' ro' : ''}${readOnly && m.match_id ? ' has-link' : ''}${selected ? ' sel' : ''}${st === 'live' ? ' live' : ''}${st === 'decision' ? ' decision' : ''}${st === 'bye' || st === 'walkover' ? ' bye' : ''}`}
      onClick={() => {
        if (!clickable) return;
        if (readOnly) { window.location.href = `${linkPrefix}/${m.match_id}`; return; }
        onSelect && onSelect(m);
      }}>
      <div className="br-m-top">
        <span className="code">{m.code}</span>
        <span>BO{m.best_of || 1}</span>
        <span className={`st st-${st}`}>{statusLabel(t, st)}</span>
      </div>
      {row(1)}
      {/* a group bye shows its empty side as "BYE"; elsewhere a bye / walkover card keeps one row */}
      {m.team2_id || !(st === 'bye' || st === 'walkover') || String(m.bracket_side || '').startsWith('group_') ? row(2) : null}
    </div>
  );
}

/** Drag & drop state for a list of MatchCards (same rules as the server's swapSlots). */
export function useCardDnd(onSwap, matches) {
  const [dragSlot, setDragSlot] = React.useState(null);
  const [dropKey, setDropKey] = React.useState(null);
  if (!onSwap) return null;
  const newestSwiss = matches.reduce((mx, x) => (x.bracket_side === 'swiss' ? Math.max(mx, x.round_number) : mx), 0);
  const canMove = (m, slot) => {
    if (!m[`team${slot}_id`] || m.match_id) return false;
    if (m.status !== 'completed') return true;
    return m.bracket_side === 'swiss' && !!m.walkover && m.round_number === newestSwiss; // the newest Swiss bye
  };
  return { dragSlot, setDragSlot, dropKey, setDropKey, onSwap, canMove };
}
